import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Input from '../Input/Input';
import Icon from '../Icon/Icon';
import createFakeEvent from '../../lib/createFakeEvent';

/**
 * `PasswordInput` is a password field that is capable of showing the password in plain text when the user clicks on an eye
 * icon in the field. The state can either be controlled or uncontrolled. You can control the state by setting either the
 * *type* directly as with any input field, or by adding a *onShowPassword*
 *
 *  * An optional notice can be displayed below the input field. See {@link Notice} for the different types.
 *
 * `PasswordInput` extends {@link Input} and accepts the same props. The ones listed below are the ones that makes the most
 * sense in the password context.
 *
 * @property {string} className The class name of this component. Will be appended to "core-password-input". {@link Input}
 * will add additional classes depending on state.
 * @property {boolean} disabled The component will be disabled if this prop is set to `true`. Defaults to `false`.
 * @property {string} id The ID of this component. Defaults to a variant of the label provided.
 * @property {string} label The label of the input field. Defaults to no label.
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {node} warningMessage A warning Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * *onUnlock* will be called if the user clicks on the lock icon.
 * @property {number} maxLength The maximum number of characters that can be entered into this input. Defaults to unlimited
 * length.
 * @property {function} onBlur A callback that will be called when the component has lost focus.
 * @property {function} onChange The change listener that will be called whenever the user changes the input value.
 * @property {function} onClearNotice Callback that is called when the user dismiss the optional Notice. * @property {boolean} locked The input field will be disabled and show a small lock icon to the right of the input field.
 * @property {function} onFocus A callback that is called when the component gets the input focus.
 * @property {function} onShowPassword A callback that is called whenever the user has clicked on the eye icon. The callback
 * will be passed an event where the `event.target.value` is the new input field type. If this callback is not set, the
 * component will handle the state internally (uncontrolled).
 * @property {string} placeholder The placeholder text for this input field. Defaults to "Password".
 * @property {boolean} readOnly Makes this input field read only. A read only input field will have a transparent background.
 * You may use *placeholder* as a default value that will be displayed when the actual value is `undefined`.
 * @property {boolean} required If set to `true`, a required indicator will be displayed inside the input field. Defaults to
 * `false`.
 * @property {boolean} showUnderline The component will display a line under the input field to indicate input focus. You
 * can set the value of this prop to `false` to turn this off. Defaults to `true`.
 * @property {string} type The type of this input field. Accepts all types supported by the HTML5 input field. Defaults to
 * "password".
 * @property {string} value The password string value.
 *
 * @see Input
 */
export default class PasswordInput extends Component {

  static displayName = 'PasswordInput';

  static propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node,
    warningMessage: PropTypes.node,
    successMessage: PropTypes.node,
    infoMessage: PropTypes.node,
    id: PropTypes.string,
    label: PropTypes.string,
    locked: PropTypes.bool,
    maxLength: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClearNotice: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onShowPassword: PropTypes.func,
    onUnlock: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool, // eslint-disable-line
    required: PropTypes.bool,
    showUnderline: PropTypes.bool,
    value: PropTypes.string,
  };

  static defaultProps = {
    placeholder: 'Password',
    showUnderline: true,
  };

  state = {};

  onTogglePassword = e => {
    if (!this.props.disabled) {
      const type = (this.props.type || this.state.type || 'password') === 'password' ? 'text' : 'password';

      if (this.props.onShowPassword) {
        this.props.onShowPassword(createFakeEvent(type, e));
      } else {
        this.setState({ type });
      }
    }
  };

  render() {
    const { className, onShowPassword, ...rest } = this.props;
    const type = this.props.type || this.state.type || 'password';

    return (
      <Input className={cx('core-password-input', className)} {...rest} type={type} clearable={false}>
        <Icon className="toggle-password" name={type === 'password' ? 'visible' : 'hidden'} onClick={this.onTogglePassword}/>
      </Input>
    );
  }

}
